/**
 * @fileoverview Security settings tab component for the profile settings page.
 * Displays multi-factor authentication status and configuration.
 */

import React from "react";
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Chip,
    Paper
} from "@mui/material";
import {
    intl
} from "dmsbasereact";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import SecurityIcon from '@mui/icons-material/Security';

/**
 * Security Tab component
 *
 * Displays the user's current multi-factor authentication (MFA) configuration.
 * Shows enabled MFA methods (email-based verification) and their status.
 * This tab is read-only and displays the security configuration without modification options.
 *
 * @param {Object} props - Component props
 * @param {Array} props.mfaMethods - Array of configured MFA methods for the user
 * @param {boolean} props.loadingMfa - Loading state for MFA data
 * @returns {JSX.Element} The security settings tab displaying MFA status
 */
export const SecurityTab = ({ mfaMethods, loadingMfa }) => {
    // Ensure mfaMethods is always an array (kept for future use)
    // eslint-disable-next-line no-unused-vars
    const methods = Array.isArray(mfaMethods) ? mfaMethods : [];

    return (
        <Box>
            {/* Header Section */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <SecurityIcon color="primary" />
                    <Typography variant="h6">
                        {intl.get("TwoFactorAuthentication").d("Two-Factor Authentication")}
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                    {intl.get("MFADescriptionMandatory").d("Your account is protected with two-factor authentication via email. A verification code is sent to your email each time you sign in.")}
                </Typography>
            </Box>

            {loadingMfa ? (
                <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                        {intl.get("Loading").d("Loading...")}
                    </Typography>
                </Box>
            ) : (
                <>
                    {/* Status Card */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            border: '1px solid',
                            borderColor: 'success.light',
                            borderRadius: 2,
                            backgroundColor: 'success.lighter'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{
                                p: 1.5,
                                borderRadius: 2,
                                backgroundColor: 'success.light',
                                color: 'success.dark',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <EmailIcon sx={{ fontSize: 32 }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Typography variant="body1" fontWeight="bold">
                                        {intl.get("EmailVerification").d("Email Verification")}
                                    </Typography>
                                    <Chip
                                        label={intl.get("Active").d("Active")}
                                        color="success"
                                        size="small"
                                        sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                    {intl.get("EmailMFAActiveDescription").d("A verification code will be sent to your email each time you sign in.")}
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>

                    {/* Status Alert */}
                    <Alert
                        severity="success"
                        sx={{ mt: 3 }}
                        icon={<CheckCircleIcon />}
                    >
                        <Typography variant="body2">
                            {intl.get("MFAEnabledMessage").d("Your account is protected with two-factor authentication.")}
                        </Typography>
                    </Alert>
                </>
            )}
        </Box>
    );
};
